import { useState, useRef, useEffect } from 'react';
import { Download, ChevronDown, Loader2, Check, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { createReport, getReport, downloadReport } from '@/features/reports/api/reports-api';
import { cn } from '@/lib/utils';

interface ExportDataDropdownProps {
  propertyId?: string;
  deviceId?: string;
  // When the export originates from a sorted device list, forwarding
  // the active sort makes the CSV/PDF row order match what the user
  // saw at export time. Optional — when omitted the backend uses its
  // default device-query order (used by the single-device export on
  // Device Details, where sort is meaningless).
  sortBy?: string;
  sortDir?: string;
}

type ExportState = 'idle' | 'generating' | 'completed' | 'failed';

export function ExportDataDropdown({ propertyId, deviceId, sortBy, sortDir }: ExportDataDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [format, setFormat] = useState<'Csv' | 'Pdf'>('Csv');
  const [exportState, setExportState] = useState<ExportState>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function handleExport() {
    setExportState('generating');
    setErrorMessage('');
    try {
      const report = await createReport({
        reportType: 'UsageSummary',
        format,
        propertyId,
        deviceId,
        sortBy,
        sortDir,
      });

      // Poll for completion
      const reportId = report.id;
      let attempts = 0;
      while (attempts < 60) {
        await new Promise((r) => setTimeout(r, 2000));
        const res = await getReport(reportId);
        const status = res.data?.status;
        if (status === 'Completed') {
          const { blob, filename } = await downloadReport(reportId);
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          // Backend sets Content-Disposition with a property-name-slugified
          // filename (e.g. `condor-labs-2-usage-2026-05-08.csv`). Forward it
          // here since the blob URL itself doesn't carry the header. The
          // fallback only fires if the header was missing or unparsable.
          a.download = filename ?? `report-${reportId}.${format === 'Csv' ? 'csv' : 'pdf'}`;
          a.click();
          URL.revokeObjectURL(url);
          setExportState('completed');
          setTimeout(() => { setExportState('idle'); setIsOpen(false); }, 2000);
          return;
        }
        if (status === 'Failed') {
          setExportState('failed');
          setErrorMessage(res.data?.errorMessage ?? 'Report generation failed');
          return;
        }
        attempts++;
      }
      setExportState('failed');
      setErrorMessage('Report generation timed out');
    } catch {
      setExportState('failed');
      setErrorMessage('Failed to start export');
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Download className="w-3.5 h-3.5" />
        Export Data
        <ChevronDown className="w-3.5 h-3.5" />
      </Button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-2 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-50 p-4 space-y-4">
          {exportState === 'generating' ? (
            <div className="flex items-center gap-3 py-4 justify-center text-sm text-gray-600 dark:text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
              Generating report...
            </div>
          ) : exportState === 'completed' ? (
            <div className="flex items-center gap-3 py-4 justify-center text-sm text-success-600">
              <Check className="w-5 h-5" />
              Download started!
            </div>
          ) : (
            <>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">Format</p>
                <div className="space-y-1">
                  <button
                    onClick={() => setFormat('Csv')}
                    className={cn(
                      'w-full text-left px-3 py-2 text-sm rounded-lg',
                      format === 'Csv'
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                    )}
                  >
                    CSV (Excel)
                  </button>
                  <button
                    onClick={() => setFormat('Pdf')}
                    className={cn(
                      'w-full text-left px-3 py-2 text-sm rounded-lg',
                      format === 'Pdf'
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                    )}
                  >
                    PDF
                  </button>
                </div>
              </div>

              {exportState === 'failed' && (
                <div className="flex items-center gap-2 text-sm text-error-600">
                  <AlertCircle className="w-4 h-4" />
                  {errorMessage}
                </div>
              )}

              <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                <Button className="w-full" onClick={handleExport}>
                  <Download className="w-4 h-4" />
                  Export
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
